<script setup>
import { onMounted, ref } from "vue";
import { useCallsStore } from "@/stores/callsStore";

const callsStore = useCallsStore();
const currTab = ref("call");

onMounted(() => {
  callsStore.fetchHistory();
});

// Row styling
function colorRowItem({ item }) {
  const selectedCid = callsStore.selectedCall?.[0]?.cid;
  const isSelected = !!selectedCid && item.cid === selectedCid;

  const classes = ["row"];

  // Emergency should keep blinking even after answer (Transmitting/Holding/etc.)
  if (item.type === "Emergency") {
    classes.push("row-emergency");
    classes.push(isSelected ? "blink-red-selected" : "blink-red");
  } else if (isSelected) {
    classes.push("selected")
    // Optional: keep green blinking for ringing non-emergency calls (matches your other component behavior)
    //if (item.status === "Ringing" && isSelected) classes.push("selected")
    //else if (item.status === "Ringing" && !isSelected) classes.push("blink-green");
  }

  //if (isSelected) classes.push("selected");

  return { class: classes.join(" ") };
}

const headersCall = [
  {
    title: "Train Num / HH Name",
    key: "trainNum",
    headerProps: { style: "font-weight: bold" },
    value: (item) => item.trainNum,
  },
  { title: "Run & Drv Nr.", key: "runAndDriveNum", headerProps: { style: "font-weight: bold" } },
  { title: "Zone", key: "zoneId", headerProps: { style: "font-weight: bold" }, sortable: false },
  { title: "Direction", key: "direction", headerProps: { style: "font-weight: bold" } },
  { title: "Status", key: "status", headerProps: { style: "font-weight: bold" } },
  {
    title: "Time",
    key: "time",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
    value: (item) => new Date(item.time).toLocaleDateString() + " " + new Date(item.time).toLocaleTimeString(),
  },
  { title: "Type", key: "type", sortable: true, headerProps: { style: "font-weight: bold" } },
  { title: "Volume", key: "volume", sortable: true, headerProps: { style: "font-weight: bold" } },
];

const headersHistCall = [
  {
    title: "Train Num / HH Name",
    key: "trainNum",
    headerProps: { style: "font-weight: bold" },
    value: (item) => item.trainNumber,
  },
  { title: "Direction", key: "direction", headerProps: { style: "font-weight: bold" } },
  { title: "Status", key: "status", headerProps: { style: "font-weight: bold" } },
  {
    title: "Start Time",
    key: "startTime",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
    value: (item) => new Date(item.startTime).toLocaleDateString() + " " + new Date(item.startTime).toLocaleTimeString(),
  },
  {
    title: "End Time",
    key: "endTime",
    sortable: true,
    headerProps: { style: "font-weight: bold" },
    value: (item) => new Date(item.endTime).toLocaleDateString() + " " + new Date(item.endTime).toLocaleTimeString(),
  },
  { title: "Type", key: "type", sortable: true, headerProps: { style: "font-weight: bold" } },
];

// Handle row selection
function handleRowSelection(_, { internalItem }) {
  const clicked = internalItem.value;
  const selectedCid = callsStore.selectedCall?.[0]?.cid;

  // toggle selection by cid (NOT object identity)
  if (selectedCid && clicked.cid === selectedCid) {
    callsStore.setSelectedCall(); // clears
  } else {
    callsStore.setSelectedCall(clicked);
  }
}
</script>

<template>
  <v-card class="h-50" style="height: 100px;">
    <v-tabs v-model="currTab" bg-color="grey" class="border-t" height="30px">
      <v-tab value="call">Individual Call</v-tab>
      <v-tab value="callHistory">Historic Individual Call</v-tab>
    </v-tabs>

    <v-card-text class="pa-0 h-100">
      <v-tabs-window v-model="currTab" style="height: 100%;">
        <v-tabs-window-item style="height: 100%;" value="call">
          <div style="height: calc(100% - 30px); overflow-y: auto;">
            <v-data-table
                v-model="callsStore.selectedCall"
                :headers="headersCall"
                :items="callsStore.activeCalls"
                :items-per-page="-1"
                :row-props="colorRowItem"
                clickable-rows
                density="compact"
                fixed-header
                hide-default-footer
                item-value="item.item"
                return-object
                select-strategy="single"
                style="height: 100%;"
                @click:row="handleRowSelection"
            />
          </div>
        </v-tabs-window-item>

        <v-tabs-window-item style="height: 100%;" value="callHistory">
          <div style="height: calc(100% - 30px); overflow-y: auto;">
            <v-data-table
                :headers="headersHistCall"
                :items="callsStore.history"
                :items-per-page="-1"
                density="compact"
                fixed-header
                hide-default-footer
                select-strategy="single"
                style="height: 100%;"
            />
          </div>
        </v-tabs-window-item>
      </v-tabs-window>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.v-card {
  border-radius: 0%;
}

:deep(.row) {
  transition: background-color 0.2s ease;
}

/* ---------- Base colors ---------- */
:deep(.row-emergency) {
  background-color: #EE5A5AFF;
  color: white;
}

:deep(.selected) {
  background-color: rgba(36, 59, 159, 0.89);
  color: white;
}

/* hover */
:deep(.row:hover) {
  background-color: rgba(172, 197, 221, 0.89);
  color: black;
}

:deep(.row-emergency:hover) {
  background-color: #933030;
  color: white;
}

/* ---------- Blinks ---------- */
@keyframes blinkGreen {
  0%   { background-color: #2ecc71; color: white }
  50%  { background-color: #ffffff; color: black }
  100% { background-color: #2ecc71; color: white }
}

@keyframes blinkRed {
  0%   { background-color: #e74c3c; color: white }
  50%  { background-color: #ffffff; color: black }
  100% { background-color: #e74c3c; color: white }
}

@keyframes blinkRedSelected {
  0%   { background-color: #8e0404; color: white }
  50%  { background-color: #e74c3c; color: white }
  100% { background-color: #8e0404; color: white }
}

:deep(.blink-green) {
  animation: blinkGreen 1s infinite;
}

:deep(.blink-red) {
  animation: blinkRed 1s infinite;
}

:deep(.blink-red-selected) {
  animation: blinkRedSelected 1s infinite;
}

/* Emergency selected: let animation own the background (avoid blue override) */
:deep(.row-emergency.selected) {
  color: white;
}
</style>
